import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, filter, finalize, take, tap } from 'rxjs/operators';

import { TurnoversService } from '@app/services/turnovers.service';
import { UnitNonPmsService } from '@app/services/unit-non-pms.service';
import { UnitsService } from '@app/services/units.service';
import {
  clearUnit,
  getRenewalStrategyForUnit,
  loadUnit,
  setCustomBreadcrumbs,
  updateUnitDoNotAutoCreateTurns,
} from '@dashboards/store/actions/dashboard.actions';
import {
  selectUnit,
  selectUnitDoNotAutoCreateTurnsLoading,
  selectUnitLoading,
} from '@dashboards/store/selectors/dashboards.selectors';
import { deleteTurnover } from '@main-application/turnovers/actions/turnovers.actions';
import { selectTurnoverDeleteStatus } from '@main-application/turnovers/selectors/turnovers.selectors';
import { UnitDetailsAbstract } from '@portfolio/components/unit-details/abstract/unit-details.abstract';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { MoveOutTypeListConst } from '@shared/constants/move-out-type-list.const';
import { EIcon } from '@shared/enums/icon.enum';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { RouteData } from '@shared/enums/route-data';
import { RoutePath } from '@shared/enums/route-path.enum';
import { UpdateStatus } from '@shared/enums/update-status';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { getEnumerationRadioListFunction } from '@shared/functions/get-enumeration-radio-list.function';
import { BreadcrumbItem } from '@shared/interfaces/breadcrumb-item';
import { EnumerationConfig } from '@shared/interfaces/enumeration-config';
import { RestUnitModel, UnitDetailsForm } from '@shared/interfaces/unit.interface';
import { UserData } from '@shared/interfaces/user-data';
import { selectLoadEnumerations } from '@shared/selectors/enumeration.selectors';
import { selectPreviousRoute } from '@shared/selectors/route-state.selector';
import { selectUserData, selectUserPermissionLevel } from '@shared/selectors/user.selectors';
import { DigitsValidator } from '@shared/validators/digits.validator';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

@UntilDestroy()
@Component({
  selector: 'app-unit-details',
  templateUrl: './unit-details.component.html',
  styleUrls: ['./unit-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitDetailsComponent extends UnitDetailsAbstract implements OnInit, OnDestroy {
  private sourceView: RoutePath;
  previousRoute?: string;
  doNotAutoCreateTurns: boolean;
  isUnitDoNotAutoCreateTurnsLoading$: Observable<boolean>;
  nameControl = this.formBuilder.control('', [Validators.required]);
  deleteInProgress$ = new BehaviorSubject(false);
  protected readonly moveOutTypeListConst = MoveOutTypeListConst;
  moveOutType = null;

  constructor(
    protected cdr: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private store: Store<{}>,
    private unitNonPmsService: UnitNonPmsService,
    private unitService: UnitsService,
    private modalsService: ModalsService,
    private snackbarService: SnackbarService,
    private turnoverService: TurnoversService
  ) {
    super(cdr);

    this.store
      .select(selectPreviousRoute)
      .pipe(untilDestroyed(this))
      .subscribe(prevRoute => (this.previousRoute = prevRoute));
  }

  ngOnInit(): void {
    this.sourceView = this.activatedRoute?.snapshot?.queryParams[RouteData.SOURCE_VIEW] as RoutePath;
    this.unitId = parseInt(this.activatedRoute.snapshot.paramMap.get('unitId'), 10);
    this.portfolioId = parseInt(this.activatedRoute.snapshot.paramMap.get('portfolioId'), 10);
    this.propertyId = parseInt(this.activatedRoute.snapshot.paramMap.get('propertyId'), 10);
    this.turnoverId = parseInt(this.activatedRoute.snapshot.paramMap.get('turnoverId'), 10);
    this.applianceId = parseInt(this.activatedRoute.snapshot.paramMap.get('applianceId'), 10);
    this.bulkBidId = this.activatedRoute?.snapshot?.queryParams[RouteData.BULK_SECTION_ID] as number;
    this.isLoading$ = this.store.select(selectUnitLoading);
    this.isUnitDoNotAutoCreateTurnsLoading$ = this.store.select(selectUnitDoNotAutoCreateTurnsLoading);

    this.nameControl.valueChanges
      .pipe(
        debounceTime(500),
        filter(name => !!name)
      )
      .subscribe(name =>
        this.unitNonPmsService
          .update({
            id: this.unit.id,
            propertyId: this.unit.propertyId,
            name: name,
          })
          .subscribe(() => {
            this.snackbarService.success(`Unit name changed to "${name}"`);
            this.unit.name = name;
            this.cdr.detectChanges();
          })
      );

    this.store.dispatch(loadUnit({ unitId: this.unitId }));
    this.store.dispatch(getRenewalStrategyForUnit({ unitId: this.unitId }));
    this.store
      .select(selectUserPermissionLevel)
      .pipe(
        untilDestroyed(this),
        filter((permissionList: PermissionLevelType[]) => !!permissionList),
        tap((permissionList: PermissionLevelType[]) => {
          this.readOnly = !checkPermissionLevel(permissionList, PermissionLevelType.PropertyManager_UnitList_Edit);
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectLoadEnumerations)
      .pipe(
        untilDestroyed(this),
        filter(value => !!value),
        tap((enumerationConfig: EnumerationConfig) => {
          this.applianceTypeList = getEnumerationRadioListFunction<number>(enumerationConfig.applianceTypes, '');
          this.colorTypeList = getEnumerationRadioListFunction<number>(enumerationConfig.applianceColors, '');
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectTurnoverDeleteStatus)
      .pipe(
        untilDestroyed(this),
        filter((turnoverDeleteStatus: UpdateStatus) => turnoverDeleteStatus === UpdateStatus.DELETED)
      )
      .subscribe(turnoverDeleteStatus => {
        if (turnoverDeleteStatus === UpdateStatus.DELETED) {
          this.snackbarService.success(`Turn ${this.nameControl.value} has been deleted`);
          this.store.dispatch(loadUnit({ unitId: this.unitId }));
        }
      });

    this.store
      .select(selectUnit)
      .pipe(
        untilDestroyed(this),
        filter((unit: RestUnitModel) => !!unit),
        tap((unit: RestUnitModel) => {
          this.unit = { ...unit };
          this.nameControl.setValue(unit.name, { emitEvent: false });
          this.doNotAutoCreateTurns = this.unit.doNotAutoCreateTurns;
          this.updateBreadcrumb();
          this.initForm();
          this.initTurnover();
          this.propertyAddress = this.getPropertyAddress(this.unit.property?.addresses?.physicalAddress);
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.store.dispatch(clearUnit());
  }
  initTurnover() {
    if (this.unit.turnoverId) {
      this.turnoverService.getTurnover(this.unit.turnoverId).subscribe(turnover => {
        this.moveOutType = turnover.moveOutType;
        this.cdr.detectChanges();
      });
    }
  }

  goBack(evt: Event) {
    evt?.preventDefault();
    this.router.navigateByUrl(this.previousRoute).then();
  }

  changeDoNotAutoCreateTurns(value: boolean) {
    this.doNotAutoCreateTurns = value;
    this.store.dispatch(
      updateUnitDoNotAutoCreateTurns({ unitId: this.unit.id, doNotAutoCreateTurns: this.doNotAutoCreateTurns })
    );

    if (this.unit?.turnoverId && value) {
      this.modalsService
        .openConfirmationModal({
          title: `${this.nameControl.value} is an active turn 😱`,
          content: 'Are you 💯 you want to delete it?',
          confirmColor: 'warn',
          confirmLabel: 'Delete',
        })
        .afterClosed()
        .pipe(
          take(1),
          tap(result => {
            if (result === DialogResult.Success) {
              this.store.dispatch(deleteTurnover({ turnoverId: this.unit.turnoverId }));
            }
          })
        )
        .subscribe();
    }
  }

  // NOTE: commented temporary, do not delete
  // backToParent(): void {
  //   if (this.sourceView) {
  //     switch (this.sourceView) {
  //       case RoutePath.JOBS_AVAILABLE_AND_BIDS_SENT:
  //         {
  //           if (this.userData.companyId) {
  //             this.router.navigate(['.'], { relativeTo: this.activatedRoute.parent }).then();
  //           } else {
  //             this.goToHome();
  //           }
  //         }
  //         break;

  //       case RoutePath.MOVE_OUT_CONFIRMATION:
  //       case RoutePath.BID:
  //       case RoutePath.RENO:
  //       case RoutePath.APPROVAL:
  //       case RoutePath.MARKETING:
  //       case RoutePath.LEASE_UP:
  //       case RoutePath.MOVE_IN:
  //       case RoutePath.ARCHIVE:
  //       case RoutePath.HISTORY:
  //         {
  //           if (this.turnoverId) {
  //             this.router
  //               .navigate([this.sourceView, this.turnoverId], {
  //                 relativeTo: this.activatedRoute.parent,
  //               })
  //               .then();
  //           } else {
  //             this.goToHome();
  //           }
  //         }
  //         break;
  //       case RoutePath.APPLIANCE_DETAIL:
  //         {
  //           if (this.turnoverId && this.applianceId) {
  //             this.router
  //               .navigate([RoutePath.BID, this.turnoverId, this.sourceView, this.applianceId], {
  //                 relativeTo: this.activatedRoute.parent,
  //               })
  //               .then();
  //           } else {
  //             this.goToHome();
  //           }
  //         }
  //         break;

  //       case RoutePath.DELINQUENCY:
  //         {
  //           this.router.navigate([AppRoutes.REPORTS_DELINQUENCIES]).then();
  //         }
  //         break;

  //       case RoutePath.UNIT_LIST:
  //         {
  //           this.router.navigate([`AppRoutes.REPORTS_RENT_ROLL]).then();
  //         }
  //         break;

  //       case RoutePath.RENEWALS:
  //         {
  //           this.router.navigate([`AppRoutes.REPORTS_RENEWALS]).then();
  //         }
  //         break;
  //       case RoutePath.RENEWALS_LIST:
  //         {
  //           this.router.navigate([AppRoutes.REPORTS_RENEWALS]).then();
  //         }
  //         break;

  //       case RoutePath.DELINQUENCY_LIST:
  //         {
  //           this.router.navigate([AppRoutes.DELINQUENCIES]).then();
  //         }
  //         break;

  //       case RoutePath.ACTIVE_TURNOVERS:
  //         {
  //           this.router.navigate([AppRoutes.TURNOVERS_ACTIVE]).then();
  //         }
  //         break;

  //       case RoutePath.ARCHIVED:
  //         {
  //           this.router.navigate([AppRoutes.TURNOVERS_ARCHIVED]).then();
  //         }
  //         break;

  //       case RoutePath.PROPERTY_DASHBOARD:
  //         {
  //           this.router.navigate([AppRoutes.PROPERTY_MANAGER_HOME_DASHBOARD]).then();
  //         }
  //         break;

  //       case RoutePath.TICKETS:
  //         {
  //           this.router.navigate([AppRoutes.TICKETS]).then();
  //         }
  //         break;

  //       case RoutePath.DASHBOARD_TICKETS:
  //         {
  //           this.router.navigate([AppRoutes.DASHBOARD_TICKETS]).then();
  //         }
  //         break;

  //       case RoutePath.MOVE_OUT_PROPERTY:
  //         {
  //           this.router.navigate([AppRoutes.MOVE_OUT_PROPERTY_MANAGER]).then();
  //         }
  //         break;

  //       case RoutePath.MOVE_OUT_PORTFOLIO:
  //         {
  //           this.router.navigate([AppRoutes.MOVE_OUT_ASSET_MANAGER]).then();
  //         }
  //         break;

  //       case RoutePath.BULK_BIDS:
  //         {
  //           this.router.navigate([AppRoutes.TURNOVERS_BULK_BIDS]).then();
  //         }
  //         break;
  //       case RoutePath.BULK_BIDS_EDIT:
  //         {
  //           this.router.navigate([`${AppRoutes.TURNOVERS_BULK_BIDS}/${RoutePath.EDIT}/${this.bulkBidId}`]).then();
  //         }
  //         break;
  //       default: {
  //         this.goToHome();
  //       }
  //     }
  //   } else {
  //     if (this.portfolioId && this.propertyId) {
  //       this.router.navigate([AppRoutes.SETTINGS_PORTFOLIO, this.portfolioId, this.propertyId]).then();
  //     } else {
  //       this.goToHome();
  //     }
  //   }
  // }

  goToHome() {
    this.router.navigate([AppRoutes.HOME]).then();
  }

  private initForm() {
    this.form = this.formBuilder.group({
      [this.UnitDetailsComponentForm.RENT]: [this.unit?.rent, [DigitsValidator(true), Validators.maxLength(5)]],
      [this.UnitDetailsComponentForm.OLDRENT]: [this.unit?.oldRent, [DigitsValidator(true), Validators.maxLength(5)]],
      [this.UnitDetailsComponentForm.MARKETRENT]: [
        this.unit?.marketRent,
        [DigitsValidator(true), Validators.maxLength(5)],
      ],
      [this.UnitDetailsComponentForm.ADVERTISEDRENT]: [
        this.unit?.advertisedRent,
        [DigitsValidator(true), Validators.maxLength(5)],
      ],
      [this.UnitDetailsComponentForm.FINISH]: [this.unit?.finish],
      [this.UnitDetailsComponentForm.DESCRIPTION]: [this.unit?.description || null, [Validators.maxLength(1024)]],
    });

    this.form.valueChanges
      .pipe(
        debounceTime(500),
        filter(() => !this.form.invalid)
      )
      .subscribe((value: UnitDetailsForm) => {
        const formValue: UnitDetailsForm = this.form.value;

        const unit: RestUnitModel = {
          ...this.unit,
          ...formValue,
          name: this.nameControl.value,
          doNotAutoCreateTurns: this.doNotAutoCreateTurns,
        };
        this.unitService.update(unit).subscribe();
      });
  }

  private updateBreadcrumb() {
    let breadcrumbList: BreadcrumbItem[] = [];

    if (this.unit?.property) {
      breadcrumbList = [
        ...breadcrumbList,
        {
          icon: EIcon.BUILDING,
          label: this.unit?.property.name,
        },
      ];
    }

    breadcrumbList = [
      ...breadcrumbList,
      {
        icon: EIcon.BUILDING,
        label: this.unit?.name,
      },
    ];

    this.store.dispatch(setCustomBreadcrumbs({ breadcrumbList }));
  }

  deleteUnit() {
    this.modalsService
      .openConfirmationModal({
        title: 'Delete unit?',
        content:
          "This will permanently delete all of this unit's data including its turn history. Flex if you are sure. 💪",
        confirmColor: 'warn',
        confirmLabel: 'Delete',
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(result => result === DialogResult.Success)
      )
      .subscribe(() => {
        this.deleteInProgress$.next(true);
        this.unitNonPmsService
          .delete(this.unitId)
          .pipe(finalize(() => this.deleteInProgress$.next(false)))
          .subscribe(() => {
            this.snackbarService.success(`${this.unit.name} deleted`);
            this.goBack(null);
          });
      });
  }
}
